import { createTheme, type Theme } from "@mui/material";

/**
 * Font stack shared with `index.css` so that the MUI components and the plain
 * DOM text render in the same typeface. Without this, MUI falls back to
 * Helvetica because Roboto isn't actually loaded.
 */
const fontFamily = [
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "Roboto",
  '"Helvetica Neue"',
  "Arial",
  "sans-serif",
].join(", ");

const monoFontFamily = [
  "ui-monospace",
  "SFMono-Regular",
  '"SF Mono"',
  "Menlo",
  "Consolas",
  "monospace",
].join(", ");

/**
 * A flat, low-chrome palette: one accent color, thin dividers instead of
 * shadows, and near-white surfaces. Keeping only a single accent is what makes
 * the graph itself the loudest thing on screen.
 */
const lightPalette = {
  accent: "#0d9488",
  accentHover: "#0f766e",
  accentSoft: "#f0fdfa",
  canvas: "#f7f8fa",
  surface: "#ffffff",
  divider: "#e4e7ec",
  textPrimary: "#101828",
  textSecondary: "#667085",
};

const darkPalette = {
  accent: "#2dd4bf",
  accentHover: "#5eead4",
  accentSoft: "rgba(45, 212, 191, 0.12)",
  canvas: "#0f1216",
  surface: "#171b21",
  divider: "rgba(255, 255, 255, 0.11)",
  textPrimary: "#e7eaee",
  textSecondary: "#98a2b3",
};

const buildTheme = (
  isDarkMode: boolean,
  isTooltipsVisible: boolean = true,
): Theme => {
  const c = isDarkMode ? darkPalette : lightPalette;

  return createTheme({
    shape: {
      // One radius for the whole app. Numeric `borderRadius` values in `sx`
      // are multiples of this, so 1 = 8px, 0.75 = 6px, and so on
      borderRadius: 8,
    },
    palette: {
      mode: isDarkMode ? "dark" : "light",
      primary: {
        main: c.accent,
        dark: c.accentHover,
        contrastText: "#ffffff",
      },
      secondary: {
        main: c.accentHover,
      },
      background: {
        default: c.canvas,
        paper: c.surface,
      },
      divider: c.divider,
      text: {
        primary: c.textPrimary,
        secondary: c.textSecondary,
      },
    },
    typography: {
      fontFamily,
      fontSize: 14,
      h6: {
        fontSize: "0.9375rem",
        fontWeight: 600,
        letterSpacing: 0,
      },
      subtitle1: {
        fontSize: "0.875rem",
        fontWeight: 600,
      },
      subtitle2: {
        fontSize: "0.8125rem",
        fontWeight: 600,
      },
      button: {
        // Shouty all-caps buttons are the single most dated thing about the
        // default Material look
        textTransform: "none",
        fontWeight: 600,
      },
    },
    components: {
      // Flat header that reads as part of the page, not a colored banner
      MuiAppBar: {
        defaultProps: {
          color: "inherit",
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: c.surface,
            backgroundImage: "none",
            borderBottom: `1px solid ${c.divider}`,
            color: c.textPrimary,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: c.surface,
            backgroundImage: "none",
            borderRight: `1px solid ${c.divider}`,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            "&.Mui-selected": {
              backgroundColor: c.accentSoft,
              color: c.accent,
            },
            "&.Mui-selected:hover": {
              backgroundColor: c.accentSoft,
            },
            "&.Mui-selected .MuiListItemIcon-root": {
              color: c.accent,
            },
          },
        },
      },
      // Second-level menus in the left column. Flat rows separated by hair
      // lines, so they read as a list nested under the panel title rather than
      // as a stack of floating Material cards
      MuiAccordion: {
        defaultProps: {
          elevation: 0,
          disableGutters: true,
          square: true,
        },
        styleOverrides: {
          root: {
            backgroundColor: "transparent",
            backgroundImage: "none",
            borderBottom: `1px solid ${c.divider}`,
            // Removes the pseudo-element MUI uses as a top divider, which
            // would double up with the border above
            "&:before": {
              display: "none",
            },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            minHeight: 38,
            paddingLeft: 12,
            paddingRight: 8,
            "&.Mui-expanded": {
              minHeight: 38,
            },
          },
          content: {
            margin: 0,
            "&.Mui-expanded": {
              margin: 0,
            },
          },
        },
      },
      MuiTooltip: {
        defaultProps: {
          disableHoverListener: !isTooltipsVisible,
          disableFocusListener: !isTooltipsVisible,
          disableTouchListener: !isTooltipsVisible,
        },
        styleOverrides: {
          tooltip: {
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 6,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            border: `1px solid ${c.divider}`,
            boxShadow: isDarkMode
              ? "0 12px 32px rgba(0, 0, 0, 0.5)"
              : "0 8px 24px rgba(16, 24, 40, 0.12)",
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: 13,
            borderRadius: 6,
            marginInline: 4,
          },
        },
      },
      // Make the fonts smaller
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            "& label": {
              fontSize: 14,
            },
          },
          input: {
            fontSize: 14,
          },
          listbox: {
            fontSize: 14,
          },
        },
      },
    },
  });
};

export { buildTheme, monoFontFamily };
